// The floating call tile (M70, D267): which corner it snaps to after a drag,
// where each corner sits, and whose video it shows. Pure, so it's tested
// without a browser.

export type TileCorner =
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right';

/**
 * Where a call starts minimised, until it's dragged (D267): top-right on
 * phones, clear of the composer; bottom-right on wider screens, where the
 * top right holds the open chat's call and menu buttons.
 */
export function defaultTileCorner(wide: boolean): TileCorner {
  return wide ? 'bottom-right' : 'top-right';
}

/** Movement under this many pixels is a tap (expand), not a drag. */
export const TILE_DRAG_THRESHOLD_PX = 6;

/**
 * Room kept free at each edge, on top of the safe area: the header at the
 * top, and the composer or the floating nav at the bottom, so typing a
 * message is never covered (D267).
 */
export const TILE_EDGE_GAP_PX = 12;
export const TILE_TOP_RESERVE_PX = 76;
export const TILE_BOTTOM_RESERVE_PX = 96;

/** The corner nearest to where the tile's centre was let go. */
export function nearestCorner(
  centerX: number,
  centerY: number,
  viewportWidth: number,
  viewportHeight: number
): TileCorner {
  const vertical = centerY < viewportHeight / 2 ? 'top' : 'bottom';
  const horizontal = centerX < viewportWidth / 2 ? 'left' : 'right';
  return `${vertical}-${horizontal}`;
}

export function isDrag(dx: number, dy: number): boolean {
  return Math.hypot(dx, dy) >= TILE_DRAG_THRESHOLD_PX;
}

/** CSS offsets for a corner, inside the notch and clear of the app's bars. */
export function cornerPosition(corner: TileCorner): {
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;
} {
  const [vertical, horizontal] = corner.split('-') as [
    'top' | 'bottom',
    'left' | 'right',
  ];
  const y =
    vertical === 'top'
      ? `calc(env(safe-area-inset-top) + ${TILE_TOP_RESERVE_PX}px)`
      : `calc(max(env(safe-area-inset-bottom), ${TILE_EDGE_GAP_PX}px) + ${TILE_BOTTOM_RESERVE_PX}px)`;
  const x = `calc(env(safe-area-inset-${horizontal}) + ${TILE_EDGE_GAP_PX}px)`;
  return { [vertical]: y, [horizontal]: x };
}

/**
 * Whose video fills the tile: whoever is talking, else whoever was shown
 * last (so it doesn't flick back when they pause), else the first person.
 */
export function pickMainRemote(
  remoteIds: string[],
  speaking: ReadonlySet<string>,
  previous: string | null
): string | null {
  if (remoteIds.length === 0) return null;
  const talking = remoteIds.filter((id) => speaking.has(id));
  if (previous && talking.includes(previous)) return previous;
  if (talking.length > 0) return talking[0] ?? null;
  if (previous && remoteIds.includes(previous)) return previous;
  return remoteIds[0] ?? null;
}
